import { inject, injectable, multiInject, optional } from 'inversify'
import { runInAction } from 'mobx'
import type { LeaveConfirmation, LeaveDecision } from '@core/routing/leave-confirmation'
import type { RouteTransition } from '@core/routing/route-transition'
import { Types } from '@core/ioc/types'
import type { UnsavedWork } from '@core/interfaces/unsaved-work'
import { DialogRepository } from '@core/infra/dialog/dialog.repository'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'

/**
 * The question the Router asks before an in-app navigation, and everything the
 * Router deliberately does not know: which dialog, in which language, and what
 * "leave anyway" means to whoever had the work.
 *
 * Lives beside ExitGuardPresenter rather than in core/routing so that routing
 * keeps no dependency on dialogs or translations — it decides *whether* to ask.
 *
 * Reads Types.IUnsavedWork directly rather than through ExitGuardPresenter:
 * this needs to filter participants down to the ones actually affected by one
 * specific transition (`isAffectedBy`), while ExitGuardPresenter's own job —
 * the `beforeunload` warning — is deliberately "ask everyone", since document
 * exit affects every route (spec §15.1).
 */
@injectable()
export class LeaveConfirmationService implements LeaveConfirmation {
  @multiInject(Types.IUnsavedWork)
  @optional()
  private participants: UnsavedWork[] = []

  @inject(DialogRepository)
  private dialogRepository!: DialogRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  prepare(transition: RouteTransition): Readonly<{ confirm(): Promise<LeaveDecision> }> | null {
    const affected = this.participants.filter(
      (participant) => participant.hasUnsavedWork() && participant.isAffectedBy(transition),
    )
    if (affected.length === 0) return null

    return {
      confirm: async (): Promise<LeaveDecision> => {
        const confirmed = await this.dialogRepository.confirm(this.i18nRepository.t('navigation.unsavedChanges'), {
          confirmLabel: this.i18nRepository.t('navigation.leave'),
          cancelLabel: this.i18nRepository.t('navigation.stay'),
        })

        if (!confirmed) return { kind: 'stay' }

        // Discarding here — before the caller knows whether the route will
        // actually commit — is exactly the bug this two-phase shape replaces
        // (spec §20.1): a failed hook or a stale/replaced navigation must not
        // lose work. `commitDiscard` only runs the discard; the Router decides
        // when, by calling it after the transition commits.
        return {
          kind: 'leave',
          commitDiscard: () =>
            runInAction(() => {
              affected.forEach((participant) => participant.discardUnsavedWork())
            }),
        }
      },
    }
  }
}
